import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { nanoid } from 'nanoid'
import { Collapse, Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap'

import FilterRow from '../FilterRow'
import { useToggle } from '../utils/hooks'

const FilterModal = ({ isOpen, filters, setFilters, filterOptions, schema, groups = [], getGroups, saveGroup, deleteGroup, editGroup }) => {
  const [isGetGroupsCalled, toggleGetGroupsCalled] = useToggle()
  const [isApplyGroupOpen, toggleApplyGroup] = useToggle()
  const [isSaveGroupOpen, toggleSaveGroup] = useToggle()
  const [isEditGroupOpen, toggleEditGroup] = useToggle()

  const [newGroupName, setNewGroupName] = useState('')
  const [searchGroupKey, setSearchGroupKey] = useState('')
  const [currentGroup, setCurrentGroup] = useState({})
  const [newFilterName, setFilterName] = useState('')
  const [filteredGroups, setFilteredGroups] = useState(groups)

  const resetStates = () => {
    setCurrentGroup({})
    setFilterName('')
    setSearchGroupKey('')
    toggleEditGroup(false)
  }

  const addFilter = () => {
    const newFilter = {
      id: nanoid(),
      filterKey: '',
      type: '',
      operatorValue: '',
      filterValue: ''
    }
    setFilters([...filters, newFilter])
  }

  const updateFilter = (id, filter) => {
    const updated = filters.map((f) => (f.id === id
      ? filter
      : f))
    setFilters(updated)
  }

  const deleteFilter = (filter) => {
    const deleted = filters.filter((f) => f.id !== filter.id)
    setFilters(deleted)
  }

  useEffect(() => {
    if (groups && groups.length > 0) setFilteredGroups(groups)
  }, [groups])

  useEffect(() => {
    if (isOpen) {
      if (filters.length === 0) addFilter()
      if (getGroups && !isGetGroupsCalled) {
        getGroups()
        toggleGetGroupsCalled(true)
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (groups && groups.length > 0) {
      const filtered = groups?.filter((group) => group.name.toLowerCase().includes(searchGroupKey.toLowerCase()))
      setFilteredGroups(filtered)
    }
  }, [searchGroupKey])

  return (
    <Collapse isOpen={isOpen}>
      <div className='row'>

        {filters.map((filter, i) => (
          <FilterRow
            key={i}
            index={i}
            schema={schema}
            filterOptions={filterOptions}
            filter={filter}
            updateFilter={updateFilter}
            deleteFilter={deleteFilter} />
        ))}
        <div className='col mb-3'>
          <button
            type='button'
            className='btn btn-add-filter f-semibold'
            onClick={addFilter}
          >
            <span className='fas fa-plus-circle' /> Add Filter
          </button>

          {saveGroup && deleteGroup && editGroup && (
            <Fragment>
              <Dropdown
                isOpen={isApplyGroupOpen}
                toggle={toggleApplyGroup}
                className='d-inline'
              >
                <DropdownToggle className='btn btn-add-filter f-semibold' tag='button' data-cy='filter-group-button'>
                  <span className='fas fa-filters' /> Apply Filter Group
                </DropdownToggle>
                <DropdownMenu className='p-0'>
                  <div>
                    {groups?.length > 0 && (
                      <div className='m-2'>
                        <div className='input-group d-flex flex-nowrap input-group-sm align-items-center border rounded'>
                          <span className='input-group-prepend'><span className='fas fa-search mx-2 text-muted' /></span>
                          <input
                            id='searchGroup'
                            style={{ minWidth: '250px', height: '2.5rem', fontSize: '.875rem', border: 'none' }}
                            className='form-control'
                            type='text'
                            aria-label='searchGroup'
                            name='searchGroup'
                            placeholder='Search...'
                            onChange={(e) => setSearchGroupKey(e.target.value)}
                            value={searchGroupKey} />
                          {searchGroupKey && (
                            <span className='input-group-prepend px-2' onClick={() => setSearchGroupKey('')} style={{ position: 'absolute', right: 0, zIndex: 10 }}>
                              <span className='fas fa-times text-muted pointer' />
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {groups?.length > 0
                      ? filteredGroups.map((d, i) => (
                        <div key={i} className='p-2'>
                          <span ></span>
                          {(isEditGroupOpen && currentGroup.uuid === d.uuid)
                            ? <div className='d-flex '>
                              <input
                                id='newFilterName'
                                style={{ minWidth: '200px', height: '2.5rem', fontSize: '.875rem' }}
                                className='form-control me-2'
                                type='text'
                                aria-label='newFilterName'
                                name='newFilterName'
                                placeholder='Name...'
                                onChange={(e) => setFilterName(e.target.value)}
                                value={newFilterName} />
                              <div className='d-flex'>
                                <button
                                  type='button'
                                  className='btn btn-success btn-sm me-2'
                                  style={{ width: '35px' }}
                                  onClick={() => {
                                    if (newFilterName && newFilterName !== currentGroup?.name) {
                                      editGroup(currentGroup.uuid, { name: newFilterName })
                                    }
                                    resetStates()
                                  }}>
                                  <span className='fas fa-check' />
                                </button>
                                <button
                                  type='button'
                                  className='btn btn-outline-danger btn-sm'
                                  style={{ width: '35px' }}
                                  onClick={() => resetStates()}>
                                  <span className='fas fa-times' />
                                </button>
                              </div>
                            </div>
                            : <div className='d-flex align-items-center justify-content-between my-1 px-2'>
                              <div
                                className='text-link'
                                style={{ maxWidth: '180px' }}
                                onClick={() => {
                                  setFilters(d.filters)
                                  toggleApplyGroup(false)
                                }}
                              >
                                {d.name}
                              </div>
                              <div>
                                <span className={`fas fa-pen pointer my-2 ms-2 ${isEditGroupOpen ? 'text-muted-light' : 'text-muted '}`} onClick={() => {
                                  setCurrentGroup(d)
                                  toggleEditGroup()
                                }} />
                                <span className={`fas fa-times pointer my-2 ms-3 ${isEditGroupOpen ? 'text-muted-light' : 'text-muted '}`} onClick={() => {
                                  deleteGroup(d.uuid)
                                  setSearchGroupKey('')
                                }} />
                              </div>
                            </div>}
                        </div>
                      ))
                      : <div className='px-3 py-1'>No Groups</div>
                    }
                  </div>
                </DropdownMenu>
              </Dropdown>

              {filters.length > 0 && filters[0].filterValue && (
                <Dropdown
                  isOpen={isSaveGroupOpen}
                  toggle={toggleSaveGroup}
                  className='d-inline'
                >
                  <DropdownToggle className='btn btn-add-filter f-semibold' tag='button' data-cy='filter-group-button'>
                    <span><span className='fas fa-save' />{` Save (${filters.length}) Filters as Group`}</span>
                  </DropdownToggle>
                  <DropdownMenu className=''>
                    <div className='form-group col-12 me-3 mb-2'>
                      <label htmlFor='newGroupName'>Filter Group Name</label>
                      <div className='d-flex'>
                        <input
                          id='newGroupName'
                          style={{ minWidth: '200px', height: '2.5rem', fontSize: '.875rem' }}
                          className='form-control me-2'
                          type='text'
                          aria-label='newGroupName'
                          name='newGroupName'
                          placeholder='Name...'
                          onChange={(e) => setNewGroupName(e.target.value)}
                          value={newGroupName} />
                        <button
                          type='button'
                          disabled={newGroupName.length === 0}
                          className='btn btn-success btn-sm me-2'
                          onClick={() => {
                            toggleSaveGroup(false)
                            const payload = {
                              name: newGroupName,
                              filters: JSON.stringify(filters)
                            }
                            saveGroup(payload, () => setNewGroupName(''))
                          }}
                        >
                          <span className='fas fa-check' />
                        </button>
                        <button
                          type='button'
                          className='btn btn-outline-danger btn-sm'
                          onClick={() => {
                            setNewGroupName('')
                            toggleSaveGroup(false)
                          }}
                        >
                          <span className='fas fa-times' />
                        </button>
                      </div>
                    </div>
                  </DropdownMenu>
                </Dropdown>
              )}
            </Fragment>
          )}

        </div>
      </div>
    </Collapse>
  )
}

FilterModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  schema: PropTypes.object,
  filters: PropTypes.array,
  filterOptions: PropTypes.array,
  setFilters: PropTypes.func,
  groups: PropTypes.array,
  getGroups: PropTypes.func,
  saveGroup: PropTypes.func,
  deleteGroup: PropTypes.func,
  editGroup: PropTypes.func
}

export default FilterModal
